import { useEffect, useMemo, useState } from 'react';
import { Order } from 'shared/types/cart';
import { useFormat } from 'helpers/hooks/useFormat';
import useI18n from 'helpers/hooks/useI18n';
import mapCosts from 'helpers/utils/mapCosts';
import { Money } from 'types/entity/product';
import { useCart } from 'frontastic';
import { CostRef } from '../types';

type Props = {
  dataReference?: 'cart' | 'order';
  order?: Order;
};

export type UseCostsData = (props: Props) => {
  costsToRender: CostRef[];
  total: CostRef;
  loading: boolean;
};

const useCostsData: UseCostsData = ({ dataReference = 'cart', order }) => {
  const { transaction } = useCart();
  const { currency } = useI18n();
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const [loading, setLoading] = useState(true);

  const skeletonMoney: Money = useMemo(() => {
    return { fractionDigits: 2, centAmount: 999, currencyCode: 'USD' };
  }, []);

  const skeletonCosts = useMemo(
    () => [
      {
        key: 'subtotal',
        label: formatMessage({ id: 'summary.subtotal', defaultMessage: 'Subtotale' }),
        value: skeletonMoney,
      },
      {
        key: 'shipping',
        label:
          dataReference === 'cart' && transaction.isEstimatedShipping
            ? formatMessage({ id: 'summary.shippingEstimate', defaultMessage: 'Stima spedizione' })
            : formatMessage({ id: 'summary.shipping', defaultMessage: 'Spedizione' }),
        value: skeletonMoney,
      },
      {
        key: 'tax',
        label: formatMessage({ id: 'summary.taxes', defaultMessage: 'Tasse' }),
        value: skeletonMoney,
      },
      {
        key: 'discount',
        label: formatMessage({
          id: 'summary.discount',
          defaultMessage: 'Sconto',
        }),
        value: skeletonMoney,
      },
    ],
    [formatMessage, skeletonMoney, transaction.isEstimatedShipping, dataReference],
  ) as CostRef[];

  const costsToRender = useMemo(() => {
    const dataIsHydrated = (dataReference === 'order' && order) || (dataReference === 'cart' && !!transaction.total);

    if (!dataIsHydrated) return [];

    const costsToUse = dataReference === 'cart' ? transaction : mapCosts({ cart: order, currency });

    const costs = [...skeletonCosts].map(({ key, label }) => {
      return {
        key,
        label,
        value: costsToUse[key],
      };
    }) as CostRef[];

    return costs;
  }, [currency, dataReference, order, skeletonCosts, transaction]);

  useEffect(() => {
    if (costsToRender.length) setLoading(false);
  }, [costsToRender]);

  const total: CostRef = {
    key: 'total',
    label: formatMessage({ id: 'summary.total', defaultMessage: 'Totale' }),
    value: dataReference === 'order' ? mapCosts({ cart: order, currency }).total : transaction.total,
  };

  return { loading, costsToRender: loading ? skeletonCosts : costsToRender, total };
};

export default useCostsData;
